// @ts-check
import { WORLD, BOTS } from '../../game/tuning.js';
const TILE = WORLD.tile;

// 1-1 teaches one move at a time: meet it where failing costs nothing, then use
// it where it matters. Lessons are spliced into the original route at these
// columns; everything at or after an insertion shifts right by its width, so
// the original encounters keep their spacing.
const POUND = 22, POUND_WIDTH = 10;
const TEST = 87, TEST_WIDTH = 18;
const SLIDE = 109, SLIDE_WIDTH = 9;
/** Original column to its place in the lesson route. @param {number} col */
const at = (col) => col + (col >= POUND ? POUND_WIDTH : 0) + (col >= TEST ? TEST_WIDTH : 0) + (col >= SLIDE ? SLIDE_WIDTH : 0);
/** Lesson starts in route columns. */
export const LESSONS = Object.freeze({ pound: at(POUND) - POUND_WIDTH, test: at(TEST) - TEST_WIDTH, slide: at(SLIDE) - SLIDE_WIDTH });

/** The original computer-world route, with its lessons. Content contains no runtime state.
 * @returns {import('../../game/types.js').LevelDefinition}
 */
function defineCyberspace() {
  /** @type {import("../../game/types.js").SolidDefinition[]} */
  const solids = [];
  /** @param {number} col @param {number} row @param {import("../../game/types.js").SolidKind} [kind] @param {number} [width] @param {number} [height] */
  const block = (col, row, kind = 'cache', width = 1, height = 1) => solids.push({
    id: `solid-${solids.length}`, x: at(col) * TILE, y: row * TILE, width: width * TILE, height: height * TILE, kind,
  });
  /** @param {number} start @param {number} end @param {number} row */
  const platform = (start, end, row) => { for (let col = start; col <= end; col++) block(col, row, 'compute'); };
  block(12, 3, 'node');
  block(18, 3); block(19, 3, 'node'); block(20, 3); block(19, 6, 'node');
  for (const [col, height] of [[24, 2], [38, 3], [72, 3], [110, 3], [137, 2]]) block(col, 0, 'conduit', 2, height);
  platform(47, 50, 2); platform(55, 58, 4); platform(63, 67, 3);
  block(49, 5, 'node'); block(65, 6, 'node');
  for (const [col, height] of [[82, 1], [83, 2], [84, 3], [85, 4], [86, 4],
    [122, 1], [123, 2], [124, 3], [125, 4], [126, 5], [127, 5],
    [131, 4], [132, 3], [133, 2], [134, 1], [144, 1], [145, 2], [146, 3], [147, 3], [148, 3]]) {
    for (let row = 0; row < height; row++) block(col, row, 'compute');
  }
  platform(92, 95, 2); platform(98, 101, 4); platform(104, 107, 6);
  for (let col = 114; col <= 118; col++) block(col, 3, col % 2 ? 'node' : 'cache');
  platform(155, 162, 0);
  // The first firewall stands a tile above a single jump's peak on the flat spawn
  // approach, so only a double jump crosses it; the first upgrade waits beyond.
  // Appended last so earlier solid IDs stay stable.
  block(8, 0, 'compute', 2, 5);
  solids[solids.length - 1].sign = 'double-jump';
  /** @type {import('../../game/types.js').EnemySpawn[]} */
  const enemies = [16, 29, 42, 49, 64, 69, 90, 99, 113, 119, 140, 152].map((col, index) => ({
    id: `bot-${index}`, x: at(col) * TILE,
    y: col === 49 ? 3 * TILE : col === 64 ? 4 * TILE : col === 99 ? 5 * TILE : 0,
    vx: -BOTS.patrolSpeed, kind: col === 69 || col === 119 ? 'sentinel' : 'drone',
  }));
  for (const solid of solids) if (solid.kind === 'node') {
    const col = solid.x / TILE;
    solid.reward = col === at(12) || col === at(65) || col === at(115) || col === at(19) && solid.y === 6 * TILE ? 'upgrade' : col === at(49) ? 'overclock' : 'bit';
  }

  /** Lesson geometry in route columns, with stable names of its own.
   * @param {string} id @param {number} col @param {number} y @param {number} width @param {number} height @param {import("../../game/types.js").SolidKind} kind @param {import("../../game/types.js").Reward} [reward]
   */
  const lesson = (id, col, y, width, height, kind, reward) => solids.push({ id: `lesson-${id}`, x: col * TILE, y, width: width * TILE, height, kind, ...(reward ? { reward } : {}) });
  // Ground pound, right after the first Core upgrade. The route crosses a vault
  // whose roof is cracked caches; a step makes the roof an easy single jump.
  // Core QP pounds through onto the node inside, which holds the Pulse Module.
  // Only a pound reaches the prize, and the vault's open right side is the way out.
  const p = LESSONS.pound;
  lesson('pound-step', p + 1, 0, 1, 2 * TILE, 'compute');
  lesson('pound-wall', p + 2, 0, 1, 4 * TILE, 'compute');
  for (let n = 0; n < 3; n++) lesson(`pound-lid-${n}`, p + 3 + n, 3 * TILE, 1, TILE, 'cache');
  lesson('pound-prize', p + 4, 0, 1, TILE, 'node', 'upgrade');
  // The test: a firewall with no words, ten flat tiles past the stairs (a single
  // jump from their top reaches under eight), and a drone patrolling the landing.
  const t = LESSONS.test;
  lesson('test-firewall', t + 10, 0, 2, 5 * TILE, 'compute');
  solids[solids.length - 1].sign = 'double-jump';
  enemies.push({ id: 'lesson-test-guard', x: (t + 15) * TILE, y: 0, vx: -BOTS.patrolSpeed, kind: 'drone' });
  // Slide: a gate with a crawlspace at its foot. Standing QP (42 units) cannot
  // enter; crouched Core QP (about 39.6) fits under the 41-unit gap. A running
  // Down press slides in, and a low roof lets QP crawl through. A double jump
  // over the top still works, so the lesson never traps a player.
  const s = LESSONS.slide;
  lesson('slide-gate', s + 4, 41, 2, 5 * TILE - 41, 'compute');
  solids[solids.length - 1].sign = 'slide';

  return {
    id: 'cyberspace', name: 'The Metaverse', width: at(170) * TILE, height: 9 * TILE - WORLD.artHeight,
    spawn: 3 * TILE, finish: at(160) * TILE, solids, enemies,
    cues: [
      { from: 3 * TILE, to: 8 * TILE, text: 'Firewall ahead. Jump, then press Jump again in midair to double jump.' },
      { from: (p - 3) * TILE, to: (p + 2) * TILE, text: 'Cracked caches. With the Core upgrade, press Down in midair to ground pound through.' },
      { from: (s + 1) * TILE, to: (s + 4) * TILE, text: 'Low gap. While running, hold Down to slide under, or double jump over.' },
    ],
    checkpoints: [
      { id: 'lesson-checkpoint-test', x: (t + 3) * TILE, name: 'Firewall test' },
      { id: 'lesson-checkpoint-slide', x: s * TILE, name: 'Crawlspace' },
    ],
    gaps: [[32, 35], [52, 56], [76, 80], [104, 108], [128, 131]].map(([start, end]) => [at(start) * TILE, at(end) * TILE]),
  };
}
const definition = defineCyberspace();
for (const value of [...definition.solids, ...definition.enemies, ...definition.gaps, ...definition.cues ?? [], ...definition.checkpoints ?? []]) Object.freeze(value);
Object.freeze(definition.solids); Object.freeze(definition.enemies); Object.freeze(definition.gaps); Object.freeze(definition.cues); Object.freeze(definition.checkpoints);
export const CYBERSPACE = Object.freeze(definition);
